import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Calendar,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  PieChart,
  LineChart,
  DollarSign,
  Filter,
  Layers
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function AdvancedAnalytics() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('6months');
  const [compareMode, setCompareMode] = useState('previous');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [forecastData, setForecastData] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, compareMode]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, trendRes, categoryRes, forecastRes] = await Promise.all([
        axios.get(`${API_URL}/api/business/analytics/summary`, {
          params: { date_range: dateRange, compare: compareMode },
          withCredentials: true
        }),
        axios.get(`${API_URL}/api/business/analytics/trends`, {
          params: { date_range: dateRange },
          withCredentials: true
        }),
        axios.get(`${API_URL}/api/business/analytics/categories`, {
          params: { date_range: dateRange },
          withCredentials: true
        }),
        axios.get(`${API_URL}/api/business/analytics/forecast`, {
          params: { months: 3 },
          withCredentials: true
        })
      ]);

      setAnalyticsData(analyticsRes.data);
      setTrendData(trendRes.data.trends || []);
      setCategoryData(categoryRes.data.categories || []);
      setForecastData(forecastRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/api/business/analytics/export`, {
        params: { date_range: dateRange, format },
        withCredentials: true,
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('CSV exported successfully');
      } else {
        // PDF would be handled differently
        toast.success('Export completed');
      }
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${(value || 0).toFixed(1)}%`;
  };

  // Chart calculations
  const maxTrendValue = useMemo(() => {
    return Math.max(...trendData.map(d => Math.max(d.income || 0, d.expenses || 0)), 1);
  }, [trendData]);

  const totalCategoryExpense = useMemo(() => {
    return categoryData.reduce((sum, c) => sum + (c.amount || 0), 0);
  }, [categoryData]);

  // Color palette for categories
  const categoryColors = [
    'bg-emerald-500', 'bg-blue-500', 'bg-purple-500', 'bg-amber-500', 
    'bg-red-500', 'bg-cyan-500', 'bg-pink-500', 'bg-indigo-500'
  ];

  if (loading && !analyticsData) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground flex items-center gap-2">
          <BarChart3 className="w-5 h-5 animate-pulse" />
          Loading analytics...
        </div>
      </div>
    );
  }

  const summary = analyticsData?.summary || {};
  const comparison = analyticsData?.comparison || {};

  return (
    <div className="p-6 space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-[hsl(var(--accent))]" />
            Advanced Analytics
          </h1>
          <p className="text-muted-foreground mt-1">
            Deep insights into your business performance
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1month">Last Month</SelectItem>
              <SelectItem value="3months">Last 3 Months</SelectItem>
              <SelectItem value="6months">Last 6 Months</SelectItem>
              <SelectItem value="12months">Last 12 Months</SelectItem>
              <SelectItem value="ytd">Year to Date</SelectItem>
            </SelectContent>
          </Select>

          <Select value={compareMode} onValueChange={setCompareMode}>
            <SelectTrigger className="w-[160px]">
              <Layers className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="previous">vs Previous Period</SelectItem>
              <SelectItem value="yoy">vs Last Year</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon" onClick={fetchAnalytics}>
            <RefreshCw className="w-4 h-4" />
          </Button>

          <Button variant="outline" onClick={() => exportData('csv')}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards with Comparison */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Revenue */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.total_income)}</p>
                <div className={`flex items-center text-sm mt-1 ${
                  (comparison.income_change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {(comparison.income_change || 0) >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 mr-1" />
                  )}
                  {formatPercent(comparison.income_change)} vs {compareMode === 'yoy' ? 'last year' : 'prev period'}
                </div>
              </div>
              <div className="p-3 rounded-full bg-emerald-500/10">
                <TrendingUp className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Expenses */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Expenses</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.total_expenses)}</p>
                <div className={`flex items-center text-sm mt-1 ${
                  (comparison.expense_change || 0) <= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {(comparison.expense_change || 0) <= 0 ? (
                    <ArrowDownRight className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                  )}
                  {formatPercent(comparison.expense_change)} vs {compareMode === 'yoy' ? 'last year' : 'prev period'}
                </div>
              </div>
              <div className="p-3 rounded-full bg-red-500/10">
                <TrendingDown className="w-6 h-6 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Profit */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit</p>
                <p className={`text-2xl font-bold ${(summary.net_profit || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(summary.net_profit)}
                </p>
                <div className={`flex items-center text-sm mt-1 ${
                  (comparison.profit_change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {(comparison.profit_change || 0) >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 mr-1" />
                  )}
                  {formatPercent(comparison.profit_change)} vs {compareMode === 'yoy' ? 'last year' : 'prev period'}
                </div>
              </div>
              <div className={`p-3 rounded-full ${(summary.net_profit || 0) >= 0 ? 'bg-blue-500/10' : 'bg-red-500/10'}`}>
                <DollarSign className={`w-6 h-6 ${(summary.net_profit || 0) >= 0 ? 'text-blue-400' : 'text-red-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profit Margin */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Profit Margin</p>
                <p className="text-2xl font-bold">{(summary.profit_margin || 0).toFixed(1)}%</p>
                <div className={`flex items-center text-sm mt-1 ${
                  (comparison.margin_change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {(comparison.margin_change || 0) >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 mr-1" />
                  )}
                  {formatPercent(comparison.margin_change)} pts
                </div>
              </div>
              <div className="p-3 rounded-full bg-purple-500/10">
                <PieChart className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue & Expense Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LineChart className="w-5 h-5" />
            Revenue vs Expenses Trend
          </CardTitle>
          <CardDescription>Monthly comparison over selected period</CardDescription>
        </CardHeader>
        <CardContent>
          {trendData.length > 0 ? (
            <>
              <div className="h-64 flex items-end gap-2">
                {trendData.map((month, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                    <div className="w-full flex items-end justify-center gap-1 h-48">
                      {/* Income bar */}
                      <div 
                        className="w-2/5 bg-emerald-500/80 rounded-t transition-all hover:bg-emerald-500 cursor-pointer relative group"
                        style={{ height: `${Math.max((month.income / maxTrendValue) * 100, 2)}%` }}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-popover border rounded px-2 py-1 text-xs opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                          {formatCurrency(month.income)}
                        </div>
                      </div>
                      {/* Expense bar */}
                      <div 
                        className="w-2/5 bg-red-500/80 rounded-t transition-all hover:bg-red-500 cursor-pointer relative group"
                        style={{ height: `${Math.max((month.expenses / maxTrendValue) * 100, 2)}%` }}
                      >
                        <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-popover border rounded px-2 py-1 text-xs opacity-0 group-hover:opacity-100 whitespace-nowrap z-10">
                          {formatCurrency(month.expenses)}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground">{month.label}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-emerald-500" />
                  <span>Revenue</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-red-500" />
                  <span>Expenses</span>
                </div>
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No trend data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Category Breakdown & Forecast */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Expense by Category
            </CardTitle>
            <CardDescription>Where your money is going</CardDescription>
          </CardHeader>
          <CardContent>
            {categoryData.length > 0 ? (
              <div className="space-y-4">
                {/* Visual pie representation */}
                <div className="flex items-center justify-center mb-6">
                  <div className="relative w-40 h-40">
                    <svg className="w-40 h-40 transform -rotate-90">
                      {categoryData.reduce((acc, cat, idx) => {
                        const percentage = (cat.amount / totalCategoryExpense) * 100;
                        const strokeDasharray = `${percentage * 3.14} 314`;
                        const strokeDashoffset = -acc.offset * 3.14;
                        acc.elements.push(
                          <circle
                            key={idx}
                            cx="80"
                            cy="80"
                            r="60"
                            fill="none"
                            strokeWidth="20"
                            stroke={`hsl(${(idx * 45) % 360} 70% 55%)`}
                            strokeDasharray={strokeDasharray}
                            strokeDashoffset={strokeDashoffset}
                          />
                        );
                        acc.offset += percentage;
                        return acc;
                      }, { elements: [], offset: 0 }).elements}
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-2xl font-bold">{formatCurrency(totalCategoryExpense)}</p>
                        <p className="text-xs text-muted-foreground">Total</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Category list */}
                <div className="space-y-3">
                  {categoryData.slice(0, 6).map((cat, idx) => {
                    const percentage = ((cat.amount / totalCategoryExpense) * 100).toFixed(0);
                    return (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: `hsl(${(idx * 45) % 360} 70% 55%)` }}
                          />
                          <span className="text-sm truncate max-w-[150px]">{cat.category}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-medium">{formatCurrency(cat.amount)}</span>
                          <Badge variant="outline" className="text-xs">{percentage}%</Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground">
                No category data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Forecast */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Revenue Forecast
            </CardTitle>
            <CardDescription>Projected revenue for next 3 months</CardDescription>
          </CardHeader>
          <CardContent>
            {forecastData?.forecast ? (
              <div className="space-y-6">
                {/* Forecast summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg bg-muted/30">
                    <p className="text-sm text-muted-foreground">Projected Revenue</p>
                    <p className="text-2xl font-bold text-emerald-400">
                      {formatCurrency(forecastData.summary?.total_projected)}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted/30">
                    <p className="text-sm text-muted-foreground">Growth Trend</p>
                    <p className={`text-2xl font-bold ${
                      (forecastData.summary?.growth_trend || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {formatPercent(forecastData.summary?.growth_trend)}
                    </p>
                  </div>
                </div>

                {/* Monthly forecast bars */}
                <div className="space-y-3">
                  {forecastData.forecast.map((month, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>{month.month_label}</span>
                        <span className="font-medium">{formatCurrency(month.projected_revenue)}</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all"
                          style={{ 
                            width: `${Math.min((month.projected_revenue / (forecastData.summary?.total_projected / 3 * 1.5)) * 100, 100)}%` 
                          }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Confidence: {month.confidence}%
                      </p>
                    </div>
                  ))}
                </div>

                {/* Forecast methodology */}
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-sm">
                  <p className="text-blue-400 font-medium">Forecast Methodology</p>
                  <p className="text-muted-foreground text-xs mt-1">
                    Based on historical trends, recurring income, and seasonal patterns.
                    Confidence decreases for further months.
                  </p>
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground">
                No forecast data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Year-over-Year Comparison */}
      {comparison.yoy_data && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Year-over-Year Comparison
            </CardTitle>
            <CardDescription>How this year compares to last year</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground mb-2">Revenue</p>
                <p className="text-lg font-bold">This Year: {formatCurrency(comparison.yoy_data?.current_revenue)}</p>
                <p className="text-sm text-muted-foreground">Last Year: {formatCurrency(comparison.yoy_data?.previous_revenue)}</p>
                <Badge className={`mt-2 ${(comparison.yoy_data?.revenue_change || 0) >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {formatPercent(comparison.yoy_data?.revenue_change)}
                </Badge>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground mb-2">Expenses</p>
                <p className="text-lg font-bold">This Year: {formatCurrency(comparison.yoy_data?.current_expenses)}</p>
                <p className="text-sm text-muted-foreground">Last Year: {formatCurrency(comparison.yoy_data?.previous_expenses)}</p>
                <Badge className={`mt-2 ${(comparison.yoy_data?.expense_change || 0) <= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {formatPercent(comparison.yoy_data?.expense_change)}
                </Badge>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/30">
                <p className="text-sm text-muted-foreground mb-2">Profit</p>
                <p className="text-lg font-bold">This Year: {formatCurrency(comparison.yoy_data?.current_profit)}</p>
                <p className="text-sm text-muted-foreground">Last Year: {formatCurrency(comparison.yoy_data?.previous_profit)}</p>
                <Badge className={`mt-2 ${(comparison.yoy_data?.profit_change || 0) >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {formatPercent(comparison.yoy_data?.profit_change)}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default AdvancedAnalytics;
